import type { HierarchicalRuleset, PreviewData } from '@library/types/rulesets';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { Spinner } from '@library/components/Spinner';
import { formatNumericalValue } from '@library/component-utils/rulesets';

export interface PreviewConfirmationModalProps {
  isOpen: boolean;
  previewData: PreviewData | null;
  handleClosePreviewModal: () => void;
  handleConfirmSubmitChanges: () => Promise<void>;
  currentRuleset: HierarchicalRuleset | undefined;
  isSubmitLoading: boolean;
}

export const PreviewConfirmationModal = ({
  isOpen,
  previewData,
  handleClosePreviewModal,
  handleConfirmSubmitChanges,
  currentRuleset,
  isSubmitLoading
}: PreviewConfirmationModalProps) => {
  if (!isOpen) return null;

  const additional = previewData
    ? previewData.total - previewData.childContent.length - 1
    : 0;
  return (
    <ConfirmationModal
      isOpen
      confirmationCopy="Save Changes"
      onCancelModal={handleClosePreviewModal}
      handleConfirm={handleConfirmSubmitChanges}
      label="Confirm Changes?"
      isLoading={isSubmitLoading}
      isConfirmDisabled={!previewData}
    >
      <>
        <p>These changes will affect the following rulesets:</p>
        {previewData ? (
          <>
            <br />
            {previewData.childContent.length === 0 && (
              <span>{currentRuleset?.attributes.name}</span>
            )}
            {previewData.childContent.length > 0 &&
              previewData.childContent.map((c) => (
                <>
                  <span>
                    {currentRuleset?.attributes.name} &gt; {c.rulesetName}
                  </span>
                  <ul className="list-disc pl-8 font-mono">
                    {c.affectedFields.map((field) => (
                      <li
                        key={`${c.rulesetName}-${field.fieldName}`}
                      >{`${field.fieldName}: ${
                        typeof field.previousValue === 'number'
                          ? formatNumericalValue({
                              fieldName: field.fieldName,
                              value: field.previousValue
                            })
                          : field.previousValue
                      } -> ${
                        typeof field.newValue === 'number'
                          ? formatNumericalValue({
                              fieldName: field.fieldName,
                              value: field.newValue
                            })
                          : field.newValue
                      }`}</li>
                    ))}
                  </ul>
                </>
              ))}
            <br />
            {additional > 0 && (
              <>
                <p>{`And ${additional} additional child rulesets.`}</p>
                <br />
              </>
            )}
            <p>Are you sure you want to save these changes?</p>
          </>
        ) : (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        )}
      </>
    </ConfirmationModal>
  );
};
